import { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    Snackbar,
    CircularProgress,
    Chip,
    Divider,
    IconButton,
    Switch,
    FormControlLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import EuroIcon from '@mui/icons-material/Euro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const SaasSettingsPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pricing, setPricing] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        loadPricing();
    }, []);

    const loadPricing = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/pricing');
            setPricing(response.data);
        } catch (error) {
            console.error('Errore caricamento prezzi:', error);
            setSnackbar({
                open: true,
                message: 'Errore nel caricamento dei prezzi',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePriceChange = (planName, field, value) => {
        setPricing(prev => prev.map(plan => 
            plan.plan_name === planName 
                ? { ...plan, [field]: value }
                : plan
        ));
    };

    const handleFeatureChange = (planName, index, value) => {
        setPricing(prev => prev.map(plan => {
            if (plan.plan_name === planName) {
                const newFeatures = [...plan.features];
                newFeatures[index] = value;
                return { ...plan, features: newFeatures };
            }
            return plan;
        }));
    };

    const addFeature = (planName) => {
        setPricing(prev => prev.map(plan => 
            plan.plan_name === planName 
                ? { ...plan, features: [...plan.features, ''] }
                : plan
        ));
    };

    const removeFeature = (planName, index) => {
        setPricing(prev => prev.map(plan => {
            if (plan.plan_name === planName) {
                const newFeatures = plan.features.filter((_, i) => i !== index);
                return { ...plan, features: newFeatures };
            }
            return plan;
        }));
    };

    const handleSave = async (planName) => {
        try {
            setSaving(true);
            const plan = pricing.find(p => p.plan_name === planName);
            
            await axios.put(`/api/admin/pricing/${planName}`, {
                display_name: plan.display_name,
                description: plan.description,
                monthly_price: parseFloat(plan.monthly_price),
                yearly_price: parseFloat(plan.yearly_price),
                yearly_discount_percent: parseInt(plan.yearly_discount_percent),
                features: plan.features.filter(f => f.trim() !== ''),
                is_popular: plan.is_popular
            });

            setSnackbar({
                open: true,
                message: 'Prezzi salvati con successo!',
                severity: 'success'
            });
            
            // Ricarica per ottenere i dati aggiornati
            await loadPricing();
        } catch (error) {
            console.error('Errore salvataggio prezzi:', error);
            setSnackbar({
                open: true,
                message: 'Errore nel salvataggio dei prezzi',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    <EuroIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
                    Gestione Prezzi
                </Typography>
                <Button
                   
                    startIcon={<RefreshIcon />}
                    onClick={loadPricing}
                >
                    Ricarica
                </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
                I prezzi configurati qui verranno mostrati nella landing page e nell'area clienti.
                Assicurati di salvare le modifiche per ogni piano.
            </Alert>

            <Grid container spacing={3}>
                {pricing.map((plan) => (
                    <Grid item xs={12} key={plan.plan_name}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h6">
                                            {plan.plan_name.toUpperCase()}
                                        </Typography>
                                        {plan.is_popular && (
                                            <Chip label="Popolare" color="primary" size="small" />
                                        )}
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={plan.is_popular}
                                                onChange={(e) => handlePriceChange(plan.plan_name, 'is_popular', e.target.checked)}
                                            />
                                        }
                                        label="Piano Popolare"
                                    />
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nome Visualizzato"
                                            value={plan.display_name}
                                            onChange={(e) => handlePriceChange(plan.plan_name, 'display_name', e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Descrizione"
                                            value={plan.description}
                                            onChange={(e) => handlePriceChange(plan.plan_name, 'description', e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Prezzo Mensile (€)"
                                            value={plan.monthly_price}
                                            onChange={(e) => handlePriceChange(plan.plan_name, 'monthly_price', e.target.value)}
                                            InputProps={{
                                                startAdornment: <EuroIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Prezzo Annuale (€)"
                                            value={plan.yearly_price}
                                            onChange={(e) => handlePriceChange(plan.plan_name, 'yearly_price', e.target.value)}
                                            InputProps={{
                                                startAdornment: <EuroIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Sconto Annuale (%)"
                                            value={plan.yearly_discount_percent}
                                            onChange={(e) => handlePriceChange(plan.plan_name, 'yearly_discount_percent', e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 2 }} />
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1">Caratteristiche</Typography>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => addFeature(plan.plan_name)}
                                            >
                                                Aggiungi
                                            </Button>
                                        </Box>
                                        {plan.features.map((feature, index) => (
                                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={feature}
                                                    onChange={(e) => handleFeatureChange(plan.plan_name, index, e.target.value)}
                                                    placeholder="Inserisci caratteristica"
                                                />
                                                <IconButton
                                                    color="error"
                                                    onClick={() => removeFeature(plan.plan_name, index)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<SaveIcon />}
                                                onClick={() => handleSave(plan.plan_name)}
                                                disabled={saving}
                                            >
                                                {saving ? 'Salvataggio...' : 'Salva Modifiche'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SaasSettingsPage;
